// Central tuning constants — single place to tweak feel without hunting.
// User-confirmed v0.1 values (Sept 2026 plan).
export const TUNING = {
  // Room: size = BASE + (n - 2) * PER_NPC px (square, auto-zoom camera).
  // Halved v0.2 for cozier encounters (2 minds ≈ 450px, 20 ≈ 1530px).
  roomBasePx: 450,
  roomPerNpcPx: 60,
  wallThicknessPx: 24,

  // Movement: wander, retarget every few seconds.
  // Doubled pace (was 5-7): rooms felt sleepy at the old speed.
  // Social steering scales with energy EXTREMES: the further energy is from
  // neutral (~5.5), the harder the bias pulls (seek when high, avoid when
  // low or cooling down). Direction thresholds below; magnitude scales.
  minSpeedPxS: 10,
  maxSpeedPxS: 14,
  retargetEveryS: [2.5, 5.0] as const, // random uniform range
  socialBiasWeight: 0.5, // base weight at extremes (0 = random, 1 = fully social)
  npcRadiusPx: 14,
  // Wall-avoid: gentle pull toward open floor so wanderers don't hug walls
  // and pin into corners (looked frozen). Applies at retarget, all energies.
  wallAvoidMarginPx: 80,
  wallAvoidWeight: 0.6,

  // Encounter: distance-only LOS in empty room (no obstacles v0.1).
  encounterDistPx: 140,
  postChatCooldownS: 60,
  busyAvoidS: 30,

  // Energy: social drains, solitude restores (+1 per 10s alone, 1-10 clamp).
  solitudeRestoreEveryS: 10,
  solitudeRestoreAmount: 1,
  talkDrainPerExchange: 1, // -1 energy per dialogue exchange while talking
  seekThreshold: 7, // energy >= 7 → bias toward others
  avoidThreshold: 3, // energy <= 3 → bias away from others

  // Dialogue lengths (user: keep exact spec).
  dialogueLengths: {
    short: [8, 10] as const,
    medium: [15, 20] as const,
    long: [30, 50] as const,
  },

  // Bubble playback: typewriter + linger by word count + click-to-skip.
  // lingerMultiplier 0.75 = -25% default (user-tunable in Setup).
  typewriterMsPerChar: 40,
  // Hold a finished line only briefly, then the next line replaces it.
  lingerBaseS: 0.4,
  lingerPerWordS: 0.05,
  lingerMultiplier: 0.75,
  bubbleFadeMs: 400,

  // LLM queue: max 4 concurrent dialogue generations; extras wait.
  maxConcurrentDialogueGen: 4,

  // Memory: keep 5 recent per direction; 6th bumps into long-term summary.
  recentMemoryKeep: 5,

  // Post-chat relationship floaters: rise + fade over this long (sim clock).
  floaterLifeS: 6,

  // Beds: crash pads for low energy. Seek radius, arrival radius, restore
  // rate (4x solitude), and the energy at which the NPC gets back up.
  bedCount: 4,
  bedMarginPx: 50,
  bedSeekRadiusPx: 220,
  bedRestRadiusPx: 26,
  bedRestoreEveryS: 2.5, // solitude is 10s → 4x faster
  bedWakeEnergy: 7,
  bedNapAfterS: 3, // continuous bed time → napping (undisturbed like deep)
  bedDeepAfterS: 45, // continuous bed time → deep sleep (push-talk only)

  // Push-talk: radial-directed interest in ONE npc (strong seek, exclusive
  // pairing, bypasses cooldown for that pair).
  pushDurationS: 120,
  pushSteerWeight: 0.85,
} as const;

// Conversation bubble palette: one color per active convo (cycles).
export const CONVO_COLORS = [
  '#ffd166',
  '#06d6a0',
  '#4cc9f0',
  '#f72585',
  '#b5179e',
  '#80ed99',
  '#ff9e00',
  '#90be6d',
] as const;

export function roomSizeForCount(n: number): number {
  const clamped = Math.min(20, Math.max(2, Math.round(n)));
  return TUNING.roomBasePx + (clamped - 2) * TUNING.roomPerNpcPx;
}

// Create World / domain tuning — Specimen Observer contracts.
export const WORLD_DOMAIN = {
  maxCharactersPerWorld: 10,
  suggestedCharactersPerWorld: 8,
  maxLearnedTraitsPerCharacter: 5,
  maxTraitReasonChars: 100,
  natureSliderMin: 0,
  natureSliderMax: 10,
  natureSliderDefault: 5,
  generatedPlotDeviceCount: 20,
  maxActivePlotDevicesPerDay: 2,
  seededPlotDeviceCatalogSize: 100,
  defaultClosedMessage: 'Come back tomorrow, to see the next episode.',
} as const;

/** Stable question ids for wizard fields (notes §§6–11). */
export const WIZARD_QUESTION_IDS = {
  groundingName: 'grounding.name',
  groundingRegion: 'grounding.region',
  groundingLanguage: 'grounding.language',
  connectedLocationType: 'connected.locationType',
  connectedDescription: 'connected.description',
  connectedCrowd: 'connected.crowd',
  connectedRhythm: 'connected.rhythm',
  periodPresent: 'period.present',
  periodPeriod: 'period.period',
  periodRationale: 'period.rationale',
} as const;

/** Wizard step order for Continue label totals (notes §5). */
export const WIZARD_BUILDER_STEPS = [
  'location-grounding',
  'connected',
  'time-period',
  'operating-hours',
] as const;

/** All valid wizard step ids — notes §5 flow (no mode / location-type screen). */
export const WIZARD_STEP_IDS = [
  'welcome',
  'location-grounding',
  'connected',
  'time-period',
  'operating-hours',
  'character-population',
  'review',
  'characters-basic',
  'characters-detailed',
  'characters-traits',
  'ready-to-launch',
  'launched',
  'saved-draft',
] as const;

/** Device selection records — v2 contract schema version. */
export const DAILY_DEVICE_SELECTION_SCHEMA_VERSION = '2.0.0' as const;
